import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import PremiumGate from '../components/PremiumGate';
import { Card, Button, Badge, Textarea, Select, Progress } from '../components/ui';
import { 
  Camera, 
  Upload, 
  AlertTriangle, 
  CheckCircle, 
  XCircle,
  Loader2,
  ThermometerSun,
  Droplets,
  Activity,
  Palette,
  Stethoscope,
  Info
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SmartCheck = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isPremium = user?.subscription_plan && user.subscription_plan !== 'free';
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    tattoo_age_days: 7,
    pain_level: 3,
    symptoms: []
  });
  
  const fileInputRef = useRef(null);

  const symptomOptions = [
    { value: 'fever', label: t('smartCheck.symptomsList.fever') },
    { value: 'itching', label: t('smartCheck.symptomsList.itching') },
    { value: 'swelling', label: t('smartCheck.symptomsList.swelling') },
    { value: 'discharge', label: t('smartCheck.symptomsList.discharge') },
    { value: 'redness', label: t('smartCheck.symptomsList.redness') },
    { value: 'pain', label: t('smartCheck.symptomsList.pain') }
  ];

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const toggleSymptom = (symptom) => {
    setFormData(prev => ({
      ...prev,
      symptoms: prev.symptoms.includes(symptom)
        ? prev.symptoms.filter(s => s !== symptom)
        : [...prev.symptoms, symptom]
    }));
  };

  const readFileAsBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleAnalyze = async () => {
    if (!image) return;

    setLoading(true);
    setResult(null);
    setError(null);

    try {
      const base64 = await readFileAsBase64(image);

      const response = await fetch(`${API_URL}/api/smart-check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          image: base64,
          tattoo_age_days: formData.tattoo_age_days,
          pain_level: formData.pain_level,
          symptoms: formData.symptoms
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setResult(data);
      } else {
        const errData = await response.json().catch(() => ({}));
        setError(errData.detail || t('smartCheck.analysisFailed', 'Analysis failed. Please try again.'));
      }
    } catch (err) {
      setError(t('smartCheck.connectionError', 'Connection error. Please check your internet.'));
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Normal': return 'success';
      case 'Uyarı': return 'warning';
      case 'Kritik': return 'destructive';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Normal': return <CheckCircle className="w-8 h-8 text-green-500" />;
      case 'Uyarı': return <AlertTriangle className="w-8 h-8 text-yellow-500" />;
      case 'Kritik': return <XCircle className="w-8 h-8 text-red-500" />;
      default: return <Info className="w-8 h-8 text-primary" />;
    }
  };

  if (!isPremium) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="max-w-2xl mx-auto px-4 py-16">
          <PremiumGate feature={t('premium.smartCheckDesc', 'AI-powered tattoo health analysis is a Premium feature. Upgrade to get instant health checks for your tattoos.')} />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <Badge className="mb-4">
            <Stethoscope className="w-3 h-3 mr-1" />
            AI Destekli
          </Badge>
          <h1 className="font-heading text-3xl mb-2" data-testid="smart-check-title">
            SMART <span className="text-primary">CHECK</span>
          </h1>
          <p className="text-muted-foreground">
            Dövmenizin fotoğrafını çekin, AI analiz etsin
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Input Section */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <Card className="p-6">
              <h2 className="font-heading text-xl mb-4">Fotoğraf Yükle</h2>
              
              {/* Image Upload */}
              <div 
                className={`border-2 border-dashed rounded-sm p-8 text-center cursor-pointer transition-colors ${
                  imagePreview ? 'border-primary' : 'border-border hover:border-primary/50'
                }`}
                onClick={() => fileInputRef.current?.click()}
              >
                {imagePreview ? (
                  <img 
                    src={imagePreview} 
                    alt="Preview" 
                    className="max-h-64 mx-auto rounded-sm"
                  />
                ) : (
                  <>
                    <Camera className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground mb-2">
                      Dövmenizin yakın çekim fotoğrafını yükleyin
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Gün ışığında, net ve odaklı çekim yapın
                    </p>
                  </>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  className="hidden"
                />
              </div>

              {/* Form Fields */}
              <div className="mt-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Dövme Yaşı (Gün)</label>
                  <Select
                    value={formData.tattoo_age_days}
                    onChange={(e) => setFormData({...formData, tattoo_age_days: parseInt(e.target.value)})}
                  >
                    <option value={1}>1 gün (Yeni)</option>
                    <option value={3}>3 gün</option>
                    <option value={7}>1 hafta</option>
                    <option value={14}>2 hafta</option>
                    <option value={21}>3 hafta</option>
                    <option value={30}>1 ay+</option>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Ağrı Seviyesi: {formData.pain_level}/10</label>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={formData.pain_level}
                    onChange={(e) => setFormData({...formData, pain_level: parseInt(e.target.value)})}
                    className="w-full accent-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Belirtiler (varsa)</label>
                  <div className="flex flex-wrap gap-2">
                    {symptomOptions.map((symptom) => (
                      <button
                        key={symptom.value}
                        type="button"
                        onClick={() => toggleSymptom(symptom.value)}
                        className={`px-3 py-1 text-sm rounded-sm border transition-colors ${
                          formData.symptoms.includes(symptom.value)
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-border hover:border-primary/50'
                        }`}
                      >
                        {symptom.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {error && (
                <div className="mt-4 p-3 bg-destructive/10 border border-destructive/30 rounded-sm flex items-center gap-2" role="alert">
                  <AlertTriangle className="w-4 h-4 text-destructive flex-shrink-0" />
                  <p className="text-sm text-destructive flex-1">{error}</p>
                  <button onClick={() => setError(null)} className="text-destructive/50 hover:text-destructive">&times;</button>
                </div>
              )}

              <Button
                className="w-full mt-6"
                onClick={handleAnalyze}
                disabled={!image || loading}
                data-testid="analyze-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    {t('smartCheck.analyzing', 'Analyzing...')}
                  </>
                ) : (
                  <>
                    <Activity className="w-4 h-4 mr-2" />
                    Analiz Et
                  </>
                )}
              </Button>
            </Card>
          </motion.div>

          {/* Results Section */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            {result ? (
              <Card className="p-6">
                <div className="flex items-center gap-4 mb-6">
                  {getStatusIcon(result.analysis?.status)}
                  <div>
                    <Badge variant={getStatusColor(result.analysis?.status)}>
                      {result.analysis?.status || 'Analiz Tamamlandı'}
                    </Badge>
                    <p className="text-sm text-muted-foreground mt-1">
                      Risk Skoru: {result.analysis?.risk_score || 'N/A'}/10
                    </p>
                  </div>
                </div>

                {/* Analysis Details */}
                {result.analysis?.analysis && (
                  <div className="space-y-4 mb-6">
                    <h3 className="font-heading text-lg">Detaylı Analiz</h3>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-3 bg-muted rounded-sm">
                        <div className="flex items-center gap-2 mb-1">
                          <ThermometerSun className="w-4 h-4 text-red-400" />
                          <span className="text-sm font-medium">Kızarıklık</span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {result.analysis.analysis.erythema || 'Normal'}
                        </p>
                      </div>
                      
                      <div className="p-3 bg-muted rounded-sm">
                        <div className="flex items-center gap-2 mb-1">
                          <Droplets className="w-4 h-4 text-blue-400" />
                          <span className="text-sm font-medium">Akıntı</span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {result.analysis.analysis.exudate || 'Normal'}
                        </p>
                      </div>
                      
                      <div className="p-3 bg-muted rounded-sm">
                        <div className="flex items-center gap-2 mb-1">
                          <Activity className="w-4 h-4 text-yellow-400" />
                          <span className="text-sm font-medium">Şişlik</span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {result.analysis.analysis.edema || 'Normal'}
                        </p>
                      </div>
                      
                      <div className="p-3 bg-muted rounded-sm">
                        <div className="flex items-center gap-2 mb-1">
                          <Palette className="w-4 h-4 text-purple-400" />
                          <span className="text-sm font-medium">Renk</span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {result.analysis.analysis.color_integrity || 'Normal'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Care Steps */}
                {result.analysis?.care_steps && (
                  <div className="mb-6">
                    <h3 className="font-heading text-lg mb-3">Bakım Önerileri</h3>
                    <ul className="space-y-2">
                      {result.analysis.care_steps.map((step, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm">
                          <CheckCircle className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                          {step}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Doctor Urgency */}
                {result.analysis?.doctor_urgency && result.analysis.doctor_urgency !== 'Gerekli Değil' && (
                  <div className="p-4 bg-destructive/10 border border-destructive/30 rounded-sm mb-6">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="w-5 h-5 text-destructive" />
                      <span className="font-medium text-destructive">Doktor Önerisi</span>
                    </div>
                    <p className="text-sm">{result.analysis.doctor_urgency}</p>
                  </div>
                )}

                {/* Disclaimer */}
                <div className="p-3 bg-muted rounded-sm">
                  <p className="text-xs text-muted-foreground">
                    {result.medical_disclaimer}
                  </p>
                </div>

                <p className="text-xs text-muted-foreground text-center mt-4">
                  {result.powered_by}
                </p>
              </Card>
            ) : (
              <Card className="p-8 text-center">
                <Stethoscope className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-heading text-xl mb-2">AI Analiz Sonucu</h3>
                <p className="text-muted-foreground">
                  Fotoğraf yükleyin ve analiz butonuna tıklayın
                </p>
              </Card>
            )}
          </motion.div>
        </div>

        {/* Info Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mt-8"
        >
          <Card className="p-6 border-primary/30">
            <h3 className="font-heading text-lg mb-4">Smart Check Nasıl Çalışır?</h3>
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="text-center">
                <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Camera className="w-6 h-6 text-primary" />
                </div>
                <h4 className="font-medium mb-1">1. Fotoğraf Çek</h4>
                <p className="text-xs text-muted-foreground">
                  Dövmenizin yakın çekim, net fotoğrafını çekin
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Activity className="w-6 h-6 text-primary" />
                </div>
                <h4 className="font-medium mb-1">2. AI Analiz</h4>
                <p className="text-xs text-muted-foreground">
                  Gemini AI görüntüyü 4 farklı kritere göre analiz eder
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <CheckCircle className="w-6 h-6 text-primary" />
                </div>
                <h4 className="font-medium mb-1">3. Sonuç Al</h4>
                <p className="text-xs text-muted-foreground">
                  Detaylı rapor ve bakım önerileri alın
                </p>
              </div>
            </div>
          </Card>
        </motion.div>
      </main>
    </div>
  );
};

export default SmartCheck;
